/**
 * 微信客服消息 tRPC API 封装
 *
 * 所有业务接口通过 tRPC router 调用（与系统其他模块一致）
 * SSE 长连接仍使用独立的 useWechatSse hook
 */

import { trpcProxyClient } from '@/lib/trpc';

// ==================== 类型导出 ====================

/** 活跃用户信息（从 tRPC 输出推导） */
export interface ActiveUser {
  openid: string;
  lastInteractionAt: string;
  interactionType: string;
  expiresAt: string;
  remainingMs: number;
  user: {
    id: string;
    username: string;
    nickname: string | null;
    avatarUrl: string | null;
  } | null;
  person: {
    id: string;
    fullName: string;
    jobNumber: string | null;
  } | null;
}

/** 对话消息 */
export interface ConversationMessage {
  id: string;
  openid: string;
  userId: string | null;
  direction: 'in' | 'out';
  msgType: string;
  content: string | null;
  operatorId: string | null;
  status: 'sent' | 'failed' | 'expired';
  readStatus: 'unread' | 'read';
  errorCode: number | null;
  errorMessage: string | null;
  createdAt: string;
  user: {
    id: string;
    username: string;
    nickname: string | null;
    avatarUrl: string | null;
  } | null;
  person: {
    id: string;
    fullName: string;
    jobNumber: string | null;
  } | null;
  operator: {
    id: string;
    username: string;
    nickname: string | null;
    avatarUrl: string | null;
  } | null;
}

/** 分页响应 */
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// ==================== tRPC 调用封装 ====================

/** 获取活跃用户列表 */
export async function fetchActiveUsers(params: {
  page?: number;
  pageSize?: number;
  keyword?: string;
}): Promise<PaginatedResponse<ActiveUser>> {
  const result = await trpcProxyClient.wechat.getActiveUsers.query({
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    ...(params.keyword ? { keyword: params.keyword } : {}),
  });
  return result as unknown as PaginatedResponse<ActiveUser>;
}

/** 获取对话历史 */
export async function fetchConversationHistory(
  openid: string,
  params?: { page?: number; pageSize?: number }
): Promise<PaginatedResponse<ConversationMessage>> {
  const result = await trpcProxyClient.wechat.getConversationHistory.query({
    openid,
    page: params?.page ?? 1,
    pageSize: params?.pageSize ?? 20,
  });
  return result as unknown as PaginatedResponse<ConversationMessage>;
}

/** 获取未读消息数量 */
export async function fetchUnreadCount(openid?: string): Promise<{ unreadCount: number }> {
  return trpcProxyClient.wechat.getUnreadCount.query({
    openid,
  });
}

/** 标记消息为已读 */
export async function markAsRead(openid: string): Promise<{ updatedCount: number }> {
  return trpcProxyClient.wechat.markAsRead.mutate({ openid });
}

/** 发送客服消息 */
export async function sendCustomMessage(
  openid: string,
  msgType: string,
  content: string
): Promise<{ success: boolean }> {
  return trpcProxyClient.wechat.sendCustomMessage.mutate({
    openid,
    msgtype: msgType as 'text' | 'image' | 'news' | 'miniprogrampage',
    content,
  });
}
